import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { setReportingUser } from '_lib/utils/sentryService';

import { BadRequestError, combineErrorsAndIssues } from '../../api/apiErrors';
import { AppNameEnum } from '../../api/apiTypes.shim';
import { BackdropSpinner } from '../../components/BackdropSpinner/BackdropSpinner';

import { Box } from '../../components/Box/Box';
import { Button } from '../../components/Button/Button';
import { ErrorDisplay } from '../../components/ErrorDisplay/ErrorDisplay';
import { IconButton } from '../../components/IconButton/IconButton';
import { InputAdornment } from '../../components/InputAdornment/InputAdornment';
import { RouterLink } from '../../components/Link/RouterLink';
import { TextField } from '../../components/TextField/TextField';
import { Typography } from '../../components/Typography/Typography';
import { AuthStorage } from '../../localStorage/authStorage';

import { AuthSuccessResult, AuthTokenNotFound } from './auth.types';
import { AuthContext } from './authContext';

export interface LoginProps {
  appName: AppNameEnum;
  onLogin?: (result: AuthSuccessResult) => Promise<void>;
}

export const Login = (props: LoginProps): React.ReactElement => {
  const { appName, onLogin } = props;

  const navigate = useNavigate();
  const { login, setAuthCaches } = useContext(AuthContext);

  const [isSaving, setIsSaving] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [validationErrors, setValidationErrors] = useState<string[]>([]);

  const { authUserCacheResource } = useContext(AuthContext);

  // TODO - move this to a react router loader
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  useEffect(() => {
    const authUser = (AuthStorage.authToken.get() && AuthStorage.currentUserId.get()) || null;
    if (authUser && onLogin) {
      const asyncLogin = async () => {
        const user = await authUserCacheResource.get();
        if (user) {
          setReportingUser(user);

          await setAuthCaches(user);
          await onLogin({ user, navigate });
        }
      };

      asyncLogin().finally(() => setIsCheckingAuth(false));
    } else {
      setIsCheckingAuth(false);
    }
  }, [authUserCacheResource, navigate, onLogin, setAuthCaches]);

  const handleLogin = useCallback(
    async (email: string, password: string) => {
      setIsSaving(true);

      try {
        if (!email || !password) {
          setValidationErrors(['email and password are required']);
          setIsSaving(false);
          return;
        }

        const result = await login({ email, password, appName });
        setValidationErrors([]);
        if (onLogin) onLogin({ ...result, navigate });
      } catch (err) {
        if (err instanceof BadRequestError) {
          const { nonFieldErrors, fieldIssues } = err.responseData || {};
          setValidationErrors(combineErrorsAndIssues(nonFieldErrors, fieldIssues));
        } else if (err instanceof AuthTokenNotFound) {
          setValidationErrors([err.messageToDisplay]);
        }
      } finally {
        setIsSaving(false);
      }
    },
    [appName, login, navigate, onLogin]
  );

  return (
    <>
      <BackdropSpinner open={isCheckingAuth || isSaving} />
      {!!validationErrors.length && (
        <ErrorDisplay errors={validationErrors} invertColors padding={1} sx={{ margin: 1, borderRadius: 2 }} />
      )}
      <TextField
        autoFocus
        fullWidth
        invertColors
        required
        autoComplete='username'
        label='email'
        size='medium'
        value={email}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <EmailIcon />
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => setEmail(e.target.value || '')}
        onEnter={() => handleLogin(email, password)}
      />
      <TextField
        fullWidth
        invertColors
        required
        autoComplete='current-password'
        label='password'
        size='medium'
        type={!showPassword ? 'password' : undefined}
        value={password}
        slotProps={{
          input: {
            startAdornment: (
              <InputAdornment position='start'>
                <LockIcon />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            )
          }
        }}
        onChange={(e) => setPassword(e.target.value || '')}
        onEnter={() => handleLogin(email, password)}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'row',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
          marginY: 1
        }}>
        <RouterLink color='primaryInverted' to='../forgot-password' variant='body1Bold'>
          Forgot password?
        </RouterLink>
        <Button
          type='button'
          variant='contained'
          color='primaryInverted'
          onClick={() => handleLogin(email, password)}
          disabled={!email || !password}>
          <Typography variant='body1Bold' text='Login' />
        </Button>
      </Box>
    </>
  );
};
